// platform/src/components/mw/MwStat.tsx
//
// Métrica grande con animación count-up al entrar en viewport.
// IntersectionObserver + requestAnimationFrame. Respeta prefers-reduced-motion.

import { useEffect, useRef, useState } from 'react'

interface MwStatProps {
  value: number
  suffix?: string
  prefix?: string
  label: string
  duration?: number
}

export function MwStat({ value, suffix, prefix, label, duration = 1200 }: MwStatProps) {
  // Si el usuario prefiere reduced-motion, mostramos el valor final
  // directamente desde el inicio (no animamos). Lo inicializamos en
  // lazy state para evitar leer window en SSR y para evitar setState
  // sincrónico dentro del effect.
  const [displayValue, setDisplayValue] = useState(() => {
    if (typeof window === 'undefined') return 0
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    return prefersReduced ? value : 0
  })
  const ref = useRef<HTMLDivElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    // Si reduced-motion, no observamos: el valor ya está fijado en el
    // initial state. Solo marcamos el flag por si la prop cambia.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      hasAnimated.current = true
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true
            const start = performance.now()

            const tick = (now: number) => {
              const elapsed = now - start
              const t = Math.min(elapsed / duration, 1)
              // Easing out cubic
              const eased = 1 - Math.pow(1 - t, 3)
              setDisplayValue(Math.floor(eased * value))
              if (t < 1) requestAnimationFrame(tick)
              else setDisplayValue(value)
            }
            requestAnimationFrame(tick)
            observer.disconnect()
          }
        }
      },
      { threshold: 0.3 },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [value, duration])

  return (
    <div ref={ref}>
      <p
        style={{
          fontFamily: 'var(--mw-font-display)',
          fontSize: 'clamp(2rem, 4.5vw, 2.5rem)',
          fontWeight: 700,
          color: 'var(--mw-ink)',
          letterSpacing: '-0.03em',
          lineHeight: 1,
          margin: 0,
        }}
      >
        {prefix}
        {displayValue}
        {suffix}
      </p>
      <p
        style={{
          fontFamily: 'var(--mw-font-body)',
          fontSize: '13px',
          color: 'var(--mw-ink-muted)',
          margin: '0.5rem 0 0',
        }}
      >
        {label}
      </p>
    </div>
  )
}
